import type { Coord3, ProfileColorMode, RouteFeature, UnitSystem } from '../types';

const routePalette = ['#2dd4bf', '#fbbf24', '#fb7185', '#60a5fa', '#a78bfa', '#34d399', '#f97316'];
const detailPalette = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#be123c', '#4f46e5', '#15803d', '#a16207'];

export type RainRadarSamples = Record<number, Record<number, number>>;

export interface ProfileBuildMode {
  layout: 'compact' | 'expanded';
  colorMode: ProfileColorMode;
  dynamic?: boolean;
  progressKm?: number;
  compactWindowKm?: number;
  currentLocation?: Coord3 | null;
}

export interface ProfileViewport {
  width: number;
  height: number;
  isVisible?: (coordinate: Coord3, bottomInset: number, index: number) => boolean;
}

export interface ProfileTemperature {
  range: { min: number; max: number };
  sample: (coordinate: Coord3) => number;
}

export interface ProfilePoint {
  km: number;
  ele: number;
  coord: Coord3;
  idx: number;
  color: string;
  detailLabel?: string;
  precip: number;
}

export interface ProfileChunk {
  color: string;
  points: ProfilePoint[];
}

export interface RainTrace {
  key: string;
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  terrainY: number;
  width: number;
  opacity: number;
  delayMs: number;
  qualitative: true;
}

export interface TemperatureOverlay {
  key: string;
  gradientId: string;
  skyPath: string;
  stops: Array<{ offset: string; color: string }>;
  markers: Array<{ key: string; x: number; label: string; color: string }>;
}

export interface ProfileRouteModel {
  route: RouteFeature;
  baseColor: string;
  points: ProfilePoint[];
  chunks: ProfileChunk[];
  rainTraces: RainTrace[];
  linePath: string;
}

export interface ProfileModel {
  layout: 'compact' | 'expanded';
  units: UnitSystem;
  width: number;
  height: number;
  pad: { left: number; right: number; top: number; bottom: number };
  profiles: ProfileRouteModel[];
  minEle: number;
  maxEle: number;
  axisMinKm: number;
  axisMaxKm: number;
  xTicks: number[];
  yTicks: number[];
  rangeLabel: string;
  x: (km: number) => number;
  y: (elevationM: number) => number;
  formatDistance: (km: number) => string;
  formatElevation: (elevationM: number) => string;
  temperatureOverlays: TemperatureOverlay[];
  locationMarker: { x: number; y: number; label: string } | null;
}

export interface ProfileCursor {
  point: ProfilePoint;
  routeName: string;
  pointX: number;
  pointY: number;
  color: string;
  label: string;
  mapFeature: GeoJSON.Feature<GeoJSON.Point, { color: string; label: string }>;
}

function haversineKm(a: Coord3, b: Coord3): number {
  const dLat = (b[1] - a[1]) * Math.PI / 180;
  const dLon = (b[0] - a[0]) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a[1] * Math.PI / 180) * Math.cos(b[1] * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function routeDistanceProfile(coordinates: Coord3[]) {
  let total = 0;
  return coordinates.map((coord, index) => {
    if (index > 0) total += haversineKm(coordinates[index - 1], coord);
    return { km: total, ele: Number(coord[2] || 0), coord, idx: index };
  });
}

export function detailValueKey(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'unknown';
  if (Array.isArray(value)) return value.join(',');
  return String(value);
}

function hashString(value: unknown): number {
  const text = detailValueKey(value);
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = ((hash << 5) - hash + text.charCodeAt(index)) | 0;
  return Math.abs(hash);
}

function profileModeLabel(mode: ProfileColorMode): string {
  return ({
    elevation: 'Elevation', max_speed: 'Max speed', average_speed: 'Average speed', road_class: 'Road class',
    surface: 'Surface', country: 'Country', toll: 'Toll', road_environment: 'Environment', road_access: 'Access', lanes: 'Lanes'
  })[mode];
}

export function formatProfileDetailValue(mode: ProfileColorMode, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'unknown';
  const number = Number(value);
  if ((mode === 'max_speed' || mode === 'average_speed') && Number.isFinite(number)) return `${Math.round(number)} km/h`;
  if (mode === 'lanes' && Number.isFinite(number)) return `${Math.round(number)} lanes`;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return detailValueKey(value).replace(/_/g, ' ');
}

export function colorForProfileDetail(mode: ProfileColorMode, value: unknown, fallback = '#2dd4bf'): string {
  if (mode === 'elevation') return fallback;
  if (value === null || value === undefined || value === '') return '#94a3b8';
  const number = Number(value);
  if ((mode === 'max_speed' || mode === 'average_speed') && Number.isFinite(number)) {
    if (number <= 30) return '#b91c1c';
    if (number <= 50) return '#f97316';
    if (number <= 80) return '#eab308';
    if (number <= 100) return '#22c55e';
    if (number <= 120) return '#06b6d4';
    return '#2563eb';
  }
  if (mode === 'lanes' && Number.isFinite(number)) {
    if (number <= 1) return '#f97316';
    if (number === 2) return '#22c55e';
    if (number === 3) return '#06b6d4';
    return '#2563eb';
  }
  if (mode === 'toll') {
    if (value === true || value === 'yes' || value === 'true') return '#dc2626';
    if (value === false || value === 'no' || value === 'false') return '#16a34a';
  }
  return detailPalette[hashString(value) % detailPalette.length];
}

function applyDetails(
  route: RouteFeature,
  profile: ReturnType<typeof routeDistanceProfile>,
  mode: ProfileColorMode,
  fallbackColor: string,
  rain: RainRadarSamples
): ProfilePoint[] {
  const ranges = route.properties.details?.[mode] || [];
  const routeRain = rain[route.properties.route_num] || {};
  let rangeIndex = 0;
  return profile.map((point) => {
    while (rangeIndex < ranges.length && point.idx > ranges[rangeIndex][1]) rangeIndex += 1;
    const range = ranges[rangeIndex];
    const value = range && point.idx >= range[0] && point.idx <= range[1] ? range[2] : null;
    return {
      ...point,
      color: colorForProfileDetail(mode, value, fallbackColor),
      precip: routeRain[point.idx] || 0,
      detailLabel: mode === 'elevation' ? undefined : `${profileModeLabel(mode)}: ${formatProfileDetailValue(mode, value)}`
    };
  });
}

function chunkProfile(points: ProfilePoint[], baseColor: string, mode: ProfileColorMode): ProfileChunk[] {
  if (mode === 'elevation') return [{ color: baseColor, points }];
  const output: ProfileChunk[] = [];
  let current: ProfilePoint[] = [];
  let currentColor = '';
  points.forEach((point) => {
    const pointColor = point.color || baseColor;
    if (current.length && pointColor !== currentColor) {
      if (current.length > 1) output.push({ color: currentColor, points: current });
      current = [current[current.length - 1], point];
    } else {
      current.push(point);
    }
    currentColor = pointColor;
  });
  if (current.length > 1) output.push({ color: currentColor || baseColor, points: current });
  return output;
}

function formatDistance(km: number, units: UnitSystem): string {
  if (units === 'imperial') {
    const miles = km * 0.621371;
    return `${miles.toFixed(miles >= 100 ? 0 : 1)} mi`;
  }
  return `${km.toFixed(km >= 100 ? 0 : 1)} km`;
}

function formatElevation(meters: number, units: UnitSystem): string {
  return units === 'imperial' ? `${Math.round(meters * 3.28084)} ft` : `${Math.round(meters)} m`;
}

function interpolateColor(stops: Array<[number, [number, number, number, number?]]>, value: number) {
  if (value <= stops[0][0]) return stops[0][1];
  if (value >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
  for (let index = 1; index < stops.length; index += 1) {
    if (value <= stops[index][0]) {
      const [aValue, aColor] = stops[index - 1];
      const [bValue, bColor] = stops[index];
      const amount = (value - aValue) / (bValue - aValue || 1);
      return aColor.map((channel, channelIndex) => {
        const start = channel ?? 255;
        return Math.round(start + ((bColor[channelIndex] ?? 255) - start) * amount);
      }) as [number, number, number, number?];
    }
  }
  return stops[stops.length - 1][1];
}

export function buildProfileModel(
  routes: RouteFeature[],
  mode: ProfileBuildMode,
  units: UnitSystem,
  viewport: ProfileViewport,
  rain: RainRadarSamples,
  temperature?: ProfileTemperature
): ProfileModel | null {
  if (!routes.length) return null;
  const width = Math.max(1, Math.round(viewport.width || 960));
  const compactWidth = width < 560;
  const legendRows = Math.max(1, routes.length);
  const height = mode.layout === 'compact'
    ? Math.max(112, Math.round(viewport.height || 132))
    : compactWidth
      ? Math.max(150, Math.min(240, 96 + legendRows * 20))
      : Math.max(150, Math.round(viewport.height || 190));
  const pad = mode.layout === 'compact'
    ? { left: 12, right: 12, top: 26, bottom: 18 }
    : compactWidth
      ? { left: 50, right: 12, top: Math.min(height - 70, 24 + legendRows * 16), bottom: 25 }
      : { left: 54, right: 22, top: 30, bottom: 32 };
  const sourceRoutes = mode.layout === 'compact' ? routes.slice(0, 1) : routes;
  const rawProfiles = sourceRoutes.map((route, routeIndex) => {
    const full = routeDistanceProfile(route.geometry.coordinates);
    let shown = full;
    if (mode.layout === 'compact' && full.length) {
      const startKm = Math.max(0, mode.progressKm || 0);
      const endKm = Math.min(full[full.length - 1].km, startKm + (mode.compactWindowKm || 40));
      shown = full.filter((point, pointIndex) => (
        (point.km >= startKm && point.km <= endKm)
        || (full[pointIndex - 1]?.km ?? -Infinity) >= startKm && (full[pointIndex - 1]?.km ?? Infinity) <= endKm
        || (full[pointIndex + 1]?.km ?? -Infinity) >= startKm && (full[pointIndex + 1]?.km ?? Infinity) <= endKm
      ));
    } else if (mode.dynamic && viewport.isVisible) {
      shown = full.filter((point, pointIndex) => (
        viewport.isVisible?.(point.coord, height, pointIndex)
        || (full[pointIndex - 1] ? viewport.isVisible?.(full[pointIndex - 1].coord, height, pointIndex - 1) : false)
        || (full[pointIndex + 1] ? viewport.isVisible?.(full[pointIndex + 1].coord, height, pointIndex + 1) : false)
      ));
    }
    const baseColor = route.properties.color || routePalette[routeIndex % routePalette.length];
    return { route, baseColor, points: applyDetails(route, shown, mode.colorMode, baseColor, rain) };
  }).filter((profile) => profile.points.length > 1);
  if (!rawProfiles.length) return null;

  const axisMinKm = Math.min(...rawProfiles.map((profile) => profile.points[0].km));
  let axisMaxKm = Math.max(...rawProfiles.map((profile) => profile.points[profile.points.length - 1].km));
  if (axisMaxKm <= axisMinKm) axisMaxKm = axisMinKm + 1;
  let minEle = Math.min(...rawProfiles.flatMap((profile) => profile.points.map((point) => point.ele)));
  let maxEle = Math.max(...rawProfiles.flatMap((profile) => profile.points.map((point) => point.ele)));
  minEle = Math.max(0, Math.floor((minEle - 80) / 100) * 100);
  maxEle = Math.ceil((maxEle + 120) / 100) * 100;
  if (maxEle <= minEle) maxEle = minEle + 100;
  const x = (km: number) => pad.left + ((km - axisMinKm) / (axisMaxKm - axisMinKm)) * (width - pad.left - pad.right);
  const y = (elevationM: number) => pad.top + (1 - (elevationM - minEle) / (maxEle - minEle)) * (height - pad.top - pad.bottom);
  const linePath = (points: ProfilePoint[], maximum = 900) => points
    .filter((_, index) => index % Math.max(1, Math.floor(points.length / maximum)) === 0 || index === points.length - 1)
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.km).toFixed(1)},${y(point.ele).toFixed(1)}`)
    .join('');

  const profiles: ProfileRouteModel[] = rawProfiles.map((profile, routeIndex) => {
    let lastRainX = -Infinity;
    const rainPoints = profile.points.filter((point) => {
      if (point.precip <= 0) return false;
      const currentX = x(point.km);
      if (currentX - lastRainX < 10) return false;
      lastRainX = currentX;
      return true;
    });
    const rainTraces = rainPoints.flatMap<RainTrace>((point) => {
      const intensity = Math.max(0.05, Math.min(1, point.precip));
      const count = Math.max(2, Math.min(7, Math.ceil(intensity * 7)));
      const terrainY = y(point.ele) - 2;
      const availableHeight = Math.max(0, terrainY - pad.top - 10);
      if (availableHeight <= 8) return [];
      return Array.from({ length: count }, (_, dropIndex) => {
        const seed = point.idx * 37 + dropIndex * 53 + routeIndex * 19;
        const xBase = x(point.km) + ((seed % 11) - 5) * 1.15;
        const yBase = pad.top + 6 + (seed % Math.max(10, Math.floor(availableHeight)));
        const length = Math.min(9, 4.2 + intensity * 5.2);
        return {
          key: `rain-${routeIndex}-${point.idx}-${dropIndex}`,
          x1: xBase,
          x2: xBase - 1.8,
          y1: Math.min(yBase, terrainY - length - 1),
          y2: Math.min(yBase + length, terrainY),
          terrainY,
          width: 0.42 + intensity * 0.32,
          opacity: Math.min(0.72, 0.2 + intensity * 0.52),
          delayMs: -((seed % 11) * 82),
          qualitative: true as const
        };
      });
    });
    return {
      ...profile,
      chunks: chunkProfile(profile.points, profile.baseColor, mode.colorMode),
      rainTraces,
      linePath: linePath(profile.points)
    };
  });

  const rangeKm = axisMaxKm - axisMinKm;
  const xStep = rangeKm > 900 ? 200 : rangeKm > 350 ? 100 : rangeKm > 150 ? 50 : rangeKm > 60 ? 20 : rangeKm > 20 ? 10 : 5;
  const xSeed = Array.from({ length: Math.floor(rangeKm / xStep) + 2 }, (_, index) => Math.ceil(axisMinKm / xStep) * xStep + index * xStep)
    .filter((value) => value <= axisMaxKm);
  const xTicks = Array.from(new Set([...xSeed, axisMaxKm].map((value) => Math.round(value * 10) / 10))).sort((a, b) => a - b);
  const yTicks = Array.from({ length: Math.floor((maxEle - minEle) / 200) + 1 }, (_, index) => Math.ceil(minEle / 200) * 200 + index * 200)
    .filter((value) => value <= maxEle);
  const displayMin = units === 'imperial' ? axisMinKm * 0.621371 : axisMinKm;
  const displayMax = units === 'imperial' ? axisMaxKm * 0.621371 : axisMaxKm;
  const rangeLabel = `${units === 'imperial' ? 'mi' : 'km'} ${Math.round(displayMin)}-${Math.round(displayMax)}`;

  const temperatureOverlays = temperature ? profiles.map((profile, routeIndex): TemperatureOverlay => {
    const sampleStep = Math.max(1, Math.floor(profile.points.length / 96));
    const samples = profile.points.filter((_, index) => index % sampleStep === 0);
    if (samples[samples.length - 1] !== profile.points[profile.points.length - 1]) samples.push(profile.points[profile.points.length - 1]);
    const stops = samples.map((point) => {
      const value = temperature.sample(point.coord);
      const color = interpolateColor(
        [[temperature.range.min, [49, 130, 206, 120]], [(temperature.range.min + temperature.range.max) / 2, [255, 237, 74, 138]], [temperature.range.max, [217, 72, 1, 154]]],
        value
      );
      return {
        offset: `${Math.max(0, Math.min(100, ((x(point.km) - pad.left) / (width - pad.left - pad.right)) * 100)).toFixed(2)}%`,
        color: `rgba(${color[0]},${color[1]},${color[2]},${((color[3] ?? 136) / 255).toFixed(3)})`
      };
    });
    const outline = profile.points.filter((_, index) => index % Math.max(1, Math.floor(profile.points.length / 700)) === 0 || index === profile.points.length - 1);
    const markerStep = Math.max(1, Math.floor(profile.points.length / (compactWidth ? 3 : 5)));
    const markers = profile.points.filter((_, index) => index % markerStep === 0).slice(0, compactWidth ? 3 : 5).map((point) => {
      const value = temperature.sample(point.coord);
      const color = interpolateColor(
        [[temperature.range.min, [49, 130, 206, 255]], [(temperature.range.min + temperature.range.max) / 2, [255, 237, 74, 255]], [temperature.range.max, [217, 72, 1, 255]]],
        value
      );
      return {
        key: `temp-marker-${routeIndex}-${point.idx}`,
        x: x(point.km),
        label: `${Math.round(units === 'imperial' ? value * 9 / 5 + 32 : value)}${units === 'imperial' ? ' F' : ' C'}`,
        color: `rgb(${color[0]},${color[1]},${color[2]})`
      };
    });
    return {
      key: `temp-overlay-${routeIndex}`,
      gradientId: `temp-profile-${mode.layout}-${routeIndex}`,
      skyPath: [
        `M${x(profile.points[0].km).toFixed(1)},${pad.top.toFixed(1)}`,
        `L${x(profile.points[profile.points.length - 1].km).toFixed(1)},${pad.top.toFixed(1)}`,
        ...outline.slice().reverse().map((point) => `L${x(point.km).toFixed(1)},${y(point.ele).toFixed(1)}`),
        'Z'
      ].join(''),
      stops,
      markers
    };
  }) : [];

  let locationMarker: ProfileModel['locationMarker'] = null;
  if (mode.currentLocation) {
    let nearest: ProfilePoint | null = null;
    let bestKm = Infinity;
    profiles.forEach((profile) => profile.points.forEach((point) => {
      const distance = haversineKm(mode.currentLocation as Coord3, point.coord);
      if (distance < bestKm) { bestKm = distance; nearest = point; }
    }));
    const locatedPoint = nearest as ProfilePoint | null;
    if (locatedPoint && bestKm <= 10) locationMarker = { x: x(locatedPoint.km), y: y(locatedPoint.ele), label: formatDistance(locatedPoint.km, units) };
  }

  return {
    layout: mode.layout, units, width, height, pad, profiles, minEle, maxEle, axisMinKm, axisMaxKm,
    xTicks, yTicks, rangeLabel, x, y,
    formatDistance: (km) => formatDistance(km, units),
    formatElevation: (elevationM) => formatElevation(elevationM, units),
    temperatureOverlays,
    locationMarker
  };
}

export function findProfileCursor(model: ProfileModel, screenX: number, screenY: number): ProfileCursor | null {
  let nearest: { point: ProfilePoint; routeName: string } | null = null;
  let best = Infinity;
  model.profiles.forEach((profile) => profile.points.forEach((point) => {
    const score = Math.abs(model.x(point.km) - screenX) + Math.abs(model.y(point.ele) - screenY) * 0.35;
    if (score < best) {
      best = score;
      nearest = { point, routeName: profile.route.properties.name };
    }
  }));
  const cursorTarget = nearest as { point: ProfilePoint; routeName: string } | null;
  if (!cursorTarget) return null;
  const point = cursorTarget.point;
  const color = point.color || '#3b82f6';
  const label = `${model.formatDistance(point.km)} / ${model.formatElevation(point.ele)}${point.detailLabel ? ` / ${point.detailLabel}` : ''}`;
  const mapLabel = `${model.formatDistance(point.km)}\n${model.formatElevation(point.ele)}${point.detailLabel ? `\n${point.detailLabel}` : ''}`;
  return {
    point,
    routeName: cursorTarget.routeName,
    pointX: model.x(point.km),
    pointY: model.y(point.ele),
    color,
    label,
    mapFeature: {
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [point.coord[0], point.coord[1]] },
      properties: { color, label: mapLabel }
    }
  };
}
